import apiClient from "./apiClient";

export interface SuggestionContext {
  suggestedStartTimeUtc: string;
  suggestedEndTimeUtc: string;
  originalDurationMinutes: number;
  suggestedLatitude?: number;
  suggestedLongitude?: number;
  suggestedOdometer?: number;
  nearestPointOfServiceId?: number;
  nearestPointOfServiceName?: string;
  suggestedEventType: string;
  gpsLogIds: number[];
}

export interface SuggestedEventMetrics {
  totalAccepted: number;
  totalDurationMinutes: number;
  averageDurationMinutes: number;
  byEventType: Record<string, number>;
  uniqueUsers: number;
}

export interface SuggestedEventMetricsRequest {
  startDate: string;
  endDate: string;
  hubId?: number;
}

// Trip Wait Summary Types
export interface WaitAction {
  actionType: string; // "Accepted" or "Dismissed"
  userEmail: string;
  userName: string;
  actionDateTimeUtc: string;
  durationMinutes: number;
  locationName?: string;
  suggestedStartTimeUtc: string;
  suggestedEndTimeUtc: string;
}

export interface TripWaitSummary {
  tripId: number;
  controlNumber: string;
  clientName: string;
  hubName: string;
  tripDate?: string;
  existingWaitCount: number;
  existingWaitMinutes: number;
  suggestedWaitCount: number;
  suggestedWaitMinutes: number;
  acceptedWaitCount: number;
  acceptedWaitMinutes: number;
  dismissedWaitCount: number;
  dismissedWaitMinutes: number;
  actions: WaitAction[];
}

export interface WaitSummaryTotals {
  totalTrips: number;
  totalExistingWaits: number;
  totalExistingWaitMinutes: number;
  totalSuggestedWaits: number;
  totalSuggestedWaitMinutes: number;
  totalAcceptedWaits: number;
  totalAcceptedWaitMinutes: number;
  totalDismissedWaits: number;
  totalDismissedWaitMinutes: number;
  acceptanceRate: number;
}

export interface TripWaitSummaryReport {
  totals: WaitSummaryTotals;
  trips: TripWaitSummary[];
  totalCount: number;
}

export interface TripWaitSummaryRequest {
  startDate: string;
  endDate: string;
  hubId?: number;
  clientId?: number;
  offset?: number;
  pageSize?: number;
}

const getMetrics = async (request: SuggestedEventMetricsRequest): Promise<SuggestedEventMetrics> => {
  const params = new URLSearchParams();
  params.append("startDate", request.startDate);
  params.append("endDate", request.endDate);
  if (request.hubId) {
    params.append("hubId", request.hubId.toString());
  }
  return apiClient.get(`/suggestedevents/metrics?${params.toString()}`);
};

const getTripWaitSummary = async (
  request: TripWaitSummaryRequest
): Promise<TripWaitSummaryReport> => {
  const params = new URLSearchParams();
  params.append("startDate", request.startDate);
  params.append("endDate", request.endDate);
  if (request.hubId) params.append("hubId", request.hubId.toString());
  if (request.clientId) params.append("clientId", request.clientId.toString());
  params.append("offset", (request.offset ?? 0).toString());
  params.append("pageSize", (request.pageSize ?? 25).toString());

  return apiClient.get(`/suggestedevents/trip-wait-summary?${params.toString()}`);
};

// ID-based methods for persisted suggested events
const acceptSuggestedEventById = async (
  suggestedEventId: number,
  eventId: number,
  waitEndEventId?: number
): Promise<void> => {
  return apiClient.post(`/suggestedevents/${suggestedEventId}/accept`, {
    eventId,
    waitEndEventId: waitEndEventId ?? null,
  });
};

const setSuggestedEventActive = async (suggestedEventId: number, isActive: boolean): Promise<void> => {
  return apiClient.put(`/suggestedevents/${suggestedEventId}/active?isActive=${isActive}`);
};

const dismissSuggestedEventsBatchById = async (
  tripId: number,
  suggestedEventIds: number[]
): Promise<void> => {
  return apiClient.post(`/suggestedevents/trip/${tripId}/dismiss-batch`, {
    suggestedEventIds,
  });
};

/** One GPS wait suggestion with its pending/accepted/dismissed status (mirror of OrderApi SuggestedEventDetail). */
export interface SuggestedEventDetail {
  id: number;
  tripId: number;
  suggestedEventTypeId: number;
  suggestedStartTimeUtc: string;
  suggestedEndTimeUtc: string;
  originalDurationMinutes: number;
  suggestedLatitude?: number;
  suggestedLongitude?: number;
  suggestedOdometer?: number;
  nearestClientPointId?: number;
  nearestPointOfServiceName?: string;
  poiName?: string;
  poiCategory?: string;
  suggestedReason?: string;
  /** Wait reason pre-filled by the API for a GPS-detected wait; blank when the rules can't defend one. */
  suggestedWaitReason?: string;
  status: "Pending" | "Accepted" | "Dismissed";
  eventId?: number;
  waitEndEventId?: number;
  acceptedByName?: string;
  acceptedDateTimeUtc?: string;
  dismissedByName?: string;
  dismissedDateTimeUtc?: string;
}

/** Lists the trip's suggested events (generates them on first look). */
const getTripSuggestedEvents = async (tripId: number): Promise<SuggestedEventDetail[]> => {
  return apiClient.get(`/suggestedevents/trip/${tripId}`);
};

const getTripStats = async (tripId: number): Promise<TripSuggestedEventStats> => {
  return apiClient.get(`/suggestedevents/trip/${tripId}/stats`);
};

export interface TripSuggestedEventStats {
  tripId: number;
  acceptedWaitCount: number;
  acceptedWaitMinutes: number;
  dismissedWaitCount: number;
  dismissedWaitMinutes: number;
}

const SuggestedEventsService = {
  getMetrics,
  getTripWaitSummary,
  getTripSuggestedEvents,
  getTripStats,
  acceptSuggestedEventById,
  setSuggestedEventActive,
  dismissSuggestedEventsBatchById,
};

export default SuggestedEventsService;
